import React, { useState } from 'react';
import { ActionConfig, Component, FormValues } from '@/types/platform';
import ComponentRenderer from './ComponentRenderer.tsx';
import './ActionButton.css';

interface ViewState {
  hasResult: boolean;
  hasSelectedFiles: boolean;
  insertedToExcel: boolean;
  formValid: boolean;
  selectedFileExtensions: Set<string>;
}

function evalConditions(cond: any, state: any): boolean {
  if (!cond) return true;

  // If anyOf is present, use OR logic
  if (cond.anyOf && cond.anyOf.length > 0) {
    return cond.anyOf.some((sub: any) => evalConditions(sub, state));
  }

  if (cond.hasResult !== undefined && cond.hasResult !== state.hasResult) return false;
  if (cond.hasSelectedFiles !== undefined && cond.hasSelectedFiles !== state.hasSelectedFiles) return false;
  if (cond.hasSelectedPanelFiles !== undefined && cond.hasSelectedPanelFiles !== (state.hasSelectedPanelFiles ?? false)) return false;
  if (cond.insertedToExcel !== undefined && cond.insertedToExcel !== state.insertedToExcel) return false;
  if (cond.hasSpreadsheetData !== undefined && cond.hasSpreadsheetData !== (state.hasSpreadsheetData ?? false)) return false;
  if (cond.formValid !== undefined && cond.formValid !== state.formValid) return false;
  if (cond.panelOpen !== undefined && cond.panelOpen !== (state.panelOpen ?? false)) return false;
  if (cond.panelFormValid !== undefined && cond.panelFormValid !== (state.panelFormValid ?? false)) return false;
  if (cond.selectedFileExtensionIn) {
    const hasMatch = cond.selectedFileExtensionIn.some((ext: string) => state.selectedFileExtensions.has(ext));
    if (!hasMatch) return false;
  }
  return true;
}

function variantClass(variant?: string, actionId?: string): string {
  if (actionId === 'convert_to_sale' || actionId === 'submit_convert_to_sale') {
    return 'result-btn result-btn-purple';
  }
  switch (variant) {
    case 'primary':   return 'result-btn result-btn-excel';
    case 'success':   return 'result-btn result-btn-csv';
    case 'secondary': return 'result-btn result-btn-new';
    case 'danger':    return 'result-btn result-btn-danger';
    case 'purple':    return 'result-btn result-btn-purple';
    default:          return 'result-btn result-btn-new';
  }
}

interface InlinePanelProps {
  action: ActionConfig;
  viewState: ViewState;
  isSubmitting: string | null;
  getTranslation: (key: string) => string;
  onExecute: (action: ActionConfig, panelFormValues?: FormValues, panelFiles?: File[]) => Promise<void>;
  onClose: () => void;
}

/**
 * Renders just the panel body (no trigger button).
 * Manages its own form state independently.
 */
const InlinePanel: React.FC<InlinePanelProps> = ({
  action,
  viewState,
  isSubmitting,
  getTranslation,
  onExecute,
  onClose,
}) => {
  const panel = action.panel!;

  const [panelFormValues, setPanelFormValues] = useState<FormValues>(() => {
    const defaults: FormValues = {};
    panel.components.forEach(comp => {
      if (comp.defaultValue !== undefined) {
        defaults[comp.name] = comp.defaultValue === 'today'
          ? new Date().toISOString().split('T')[0]
          : comp.defaultValue;
      } else {
        defaults[comp.name] = '';
      }
    });
    return defaults;
  });

  const [panelFiles, setPanelFiles] = useState<Record<string, File[]>>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [panelSuccess, setPanelSuccess] = useState(false);
  const [executedAction, setExecutedAction] = useState<ActionConfig | null>(null);

  const hasPanelFiles = Object.values(panelFiles).some(f => f.length > 0);
  const panelState = { ...viewState, hasSelectedPanelFiles: hasPanelFiles, panelOpen: true, panelFormValid: true };

  const handlePanelAction = async (panelAction: ActionConfig) => {
    setPanelError(null);
    setExecutedAction(panelAction);
    try {
      const allPanelFiles = Object.values(panelFiles).flat();
      await onExecute(panelAction, panelFormValues, allPanelFiles);
      setPanelSuccess(true);
    } catch (err: any) {
      const errorKey = panelAction.errorMessage;
      const fallbackMsg = err?.message ?? 'An error occurred.';
      setPanelError(errorKey ? getTranslation(errorKey) : fallbackMsg);
    }
  };

  const gridStyle = panel.layout === 'grid'
    ? { display: 'grid', gridTemplateColumns: `repeat(${panel.columns ?? 1}, 1fr)`, gap: '12px' }
    : { display: 'flex', flexDirection: 'column' as const, gap: '12px' };

  return (
    <div className="action-panel">
      <div className="action-panel-header">
        <div>
          <div className="action-panel-title">{getTranslation(panel.titleKey)}</div>
          {panel.descriptionKey && (
            <div className="action-panel-description">{getTranslation(panel.descriptionKey)}</div>
          )}
        </div>
        <button className="action-panel-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {panelSuccess ? (
        <div className="action-panel-success">
          <span>✓</span>
          {getTranslation(
            executedAction?.successMessageKey
            ?? action.successMessageKey
            ?? 'topAction.productList.successMessage'
          )}
        </div>
      ) : (
        <>
          <div style={gridStyle}>
            {panel.components.map((comp: Component, idx: number) => (
              <ComponentRenderer
                key={`${comp.name}-${idx}`}
                component={comp}
                value={panelFormValues[comp.name]}
                files={panelFiles[comp.name] || []}
                formValues={panelFormValues}
                onValueChange={(name, value) => setPanelFormValues(prev => ({ ...prev, [name]: value }))}
                onFileChange={(name, files) => setPanelFiles(prev => ({ ...prev, [name]: files }))}
                getTranslation={getTranslation}
              />
            ))}
          </div>

          {panelError && <div className="action-panel-error">{panelError}</div>}

          <div className="action-panel-footer">
            <button type="button" className="result-btn result-btn-new panel-footer-btn" onClick={onClose}>
              {getTranslation('postProcessing.cancel') || 'Отказ'}
            </button>
            {panel.actions.map(panelAction => {
              const visible = evalConditions(panelAction.visibleWhen, panelState);
              const enabled = evalConditions(panelAction.enabledWhen, panelState);
              if (!visible) return null;
              return (
                <button
                  key={panelAction.id}
                  type="button"
                  className={`${variantClass(panelAction.variant, panelAction.id)} panel-footer-btn`}
                  disabled={!enabled || isSubmitting !== null}
                  onClick={() => handlePanelAction(panelAction)}
                >
                  {isSubmitting === panelAction.id
                    ? getTranslation('form.submitting')
                    : getTranslation(panelAction.labelKey)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default InlinePanel;
